import { z } from "zod";

export const INPUT_MODES = ["voice", "chat"] as const;

export type InputMode = (typeof INPUT_MODES)[number];

export type OutputChannel = "audio" | "captions";

export interface ChatSettings {
  inputMode: InputMode;
  tutorAudioOn: boolean;
  captionsOn: boolean;
}

export const DEFAULT_CHAT_SETTINGS: ChatSettings = {
  inputMode: "voice",
  tutorAudioOn: true,
  captionsOn: true,
};

const chatSettingsSchema = z.object({
  inputMode: z.enum(INPUT_MODES).catch(DEFAULT_CHAT_SETTINGS.inputMode),
  tutorAudioOn: z.boolean().catch(DEFAULT_CHAT_SETTINGS.tutorAudioOn),
  captionsOn: z.boolean().catch(DEFAULT_CHAT_SETTINGS.captionsOn),
});

export const chatSettingsKey = (sessionId: string | number): string =>
  `chat_settings_${sessionId}`;

const hasSessionId = (
  sessionId: string | number | null | undefined,
): sessionId is string | number =>
  sessionId !== null && sessionId !== undefined && sessionId !== "";

export const parseChatSettings = (raw: string | null): ChatSettings => {
  try {
    return chatSettingsSchema.parse(raw ? JSON.parse(raw) : {});
  } catch {
    return { ...DEFAULT_CHAT_SETTINGS };
  }
};

export const readChatSettings = (
  sessionId: string | number | null | undefined,
  storage: Storage = sessionStorage,
): ChatSettings => {
  if (!hasSessionId(sessionId)) return { ...DEFAULT_CHAT_SETTINGS };

  try {
    return parseChatSettings(storage.getItem(chatSettingsKey(sessionId)));
  } catch {
    return { ...DEFAULT_CHAT_SETTINGS };
  }
};

export const writeChatSettings = (
  sessionId: string | number | null | undefined,
  settings: ChatSettings,
  storage: Storage = sessionStorage,
): boolean => {
  if (!hasSessionId(sessionId)) return false;

  try {
    storage.setItem(chatSettingsKey(sessionId), JSON.stringify(settings));
    return true;
  } catch {
    return false;
  }
};

export interface ChannelContext {
  chatWindowOpen?: boolean;
  captionsAvailable?: boolean;
}

export const isSilenced = (
  settings: ChatSettings,
  { chatWindowOpen = false, captionsAvailable = true }: ChannelContext = {},
): boolean =>
  !settings.tutorAudioOn &&
  !(settings.captionsOn && captionsAvailable) &&
  !chatWindowOpen;

export const defaultRepairTarget = (
  mode: InputMode,
  captionsAvailable = true,
): OutputChannel =>
  mode === "chat" && captionsAvailable ? "captions" : "audio";

export const ensureChannel = (
  settings: ChatSettings,
  channel: OutputChannel,
  { captionsAvailable = true }: ChannelContext = {},
): ChatSettings => {
  // chatWindowOpen is deliberately not threaded through: reached via
  // repairChannels the caller has already settled it, and this re-check exists
  // for direct callers, where an open window is not in play.
  if (!isSilenced(settings, { captionsAvailable })) return settings;

  return channel === "captions" && captionsAvailable
    ? { ...settings, captionsOn: true }
    : { ...settings, tutorAudioOn: true };
};

export const repairChannels = (
  settings: ChatSettings,
  {
    chatWindowOpen = false,
    captionsAvailable = true,
    target,
  }: ChannelContext & { target?: OutputChannel } = {},
): ChatSettings =>
  isSilenced(settings, { chatWindowOpen, captionsAvailable })
    ? ensureChannel(
        settings,
        target ?? defaultRepairTarget(settings.inputMode, captionsAvailable),
        { captionsAvailable },
      )
    : settings;

export const TUTOR_AUDIO_DEFERRED_COPY =
  "The audio will play in the next response";

export const CHANNEL_REPAIR_COPY =
  "Captions aren't available here, so tutor audio has been turned back on.";

export const CHANNEL_WINDOW_CLOSE_REPAIR_COPY: Record<OutputChannel, string> = {
  audio: "Closing the chat left nothing to read, so tutor audio is back on.",
  captions: "Closing the chat left nothing to hear, so captions are back on.",
};

export const CHANNEL_WINDOW_CLOSE_DEFERRED_COPY =
  "The chat stays open until this response finishes.";

export const CHANNEL_WARNING_COPY: Record<OutputChannel, string> = {
  audio:
    "Keep captions or tutor audio on. Turn on captions before muting tutor audio.",
  captions:
    "Keep captions or tutor audio on. Turn on tutor audio before turning off captions.",
};

export const resolveInputMode = (
  storedMode: InputMode,
  chatAvailable: boolean,
): InputMode => (chatAvailable ? storedMode : "voice");
